import React, { useState } from "react";
import { Sidebar as SidebarPro, Menu, MenuItem } from "react-pro-sidebar";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { usePersistStore, useStore } from "../../store";
import { TrustapUserService, UserService } from "../../services";
import DeleteAccountPopup from "../DeleteAccountPopup";
import Confirm from "../Confirm";
import Logo from "../Logo";
import ThemeToggle from "../ThemeToggle";
import { DASHBOARD_TYPE } from "../../utils/enums";
import { Utils } from "../../utils";
import GetVerifiedFirst from "../GetVerifiedFirst";
import {
  BidsIcon,
  DeleteAccountIcon,
  DashboardIcon,
  DisputesIcon,
  HelpAndSupportIcon,
  ListingsIcon,
  OpenForBidsIcon,
  PaymentsIcon,
  ProfileIcon,
  WatchlistIcon,
  LogoutIcon,
  ReviewsIcon,
  UserShield,
  BookmarkIcon,
  OpenBidsIcon,
  FollowersIcon,
  FollowingIcon,
} from "./icons";

interface SidebarProps {
  drawerOpen: boolean;
  onCloseDrawer: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ drawerOpen, onCloseDrawer }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, dashboardType, isVerifiedUser, token, user } = usePersistStore(
    (s: any) => s
  );
  const { resetFilters } = useStore((s: any) => s);
  const [showDeleteAccountPopup, setShowDeleteAccountPopup] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // Each entry is matched as "exact path OR any nested path under it",
  // so passing "/dashboard/review" highlights the menu item for both
  // "/dashboard/review" and "/dashboard/review/:adId" without falsely
  // matching siblings like "/dashboard/reviewer".
  const isActive = (paths: string[]) =>
    paths.some(
      (p) => location.pathname === p || location.pathname.startsWith(p + "/")
    );
  const [showGetVerifiedFirst, setShowGetVerifiedFirst] = useState(false);
  const [gateReason, setGateReason] = useState<
    "not-verified" | "incomplete-profile" | "no-full-trustap"
  >("not-verified");

  const guardListing = async () => {
    let hasFullTrustapAccount = false;
    if (token) {
      const result = await TrustapUserService.getMyTrustapAccount(token);
      hasFullTrustapAccount = result?.data === true;
    }
    const gate = Utils.canCreateListing(user, isVerifiedUser, hasFullTrustapAccount);
    if (gate.ok) {
      navigate("/dashboard/listing");
      return;
    }
    setGateReason(gate.reason);
    setShowGetVerifiedFirst(true);
  };

  const handleLogout = async (e: any) => {
    e.preventDefault();
    try {
      if (token) await UserService.logoutAll(token);
    } catch (err) { }
    logout();
    window.location.href = "/login";
  };

  return (
    <>
      <GetVerifiedFirst
        show={showGetVerifiedFirst}
        onHide={() => setShowGetVerifiedFirst(false)}
        reason={gateReason}
        onSuccess={() => {
          setShowGetVerifiedFirst(false);
          navigate("/dashboard/profile");
        }}
        onCompleteProfile={() => {
          setShowGetVerifiedFirst(false);
          navigate("/dashboard/profile");
        }}
        onSetUpTrustap={() => {
          setShowGetVerifiedFirst(false);
          navigate("/dashboard/trustap-account");
        }}
      />
      <SidebarPro
        breakPoint="xl"
        toggled={drawerOpen}
        onBackdropClick={onCloseDrawer}
        width="300px"
      >
        <div className="sidebar__topRow">
          <Link to="/" onClick={resetFilters} className="sidebar__brand">
            <Logo width={56} height={56} />
            <span className="sidebar__brandWordmark">Boll-e</span>
          </Link>
          <button
            type="button"
            className="sidebarCloseBtn d-xl-none"
            aria-label="Close menu"
            onClick={onCloseDrawer}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        <Menu className="sidebar__menu">
          <MenuItem
            active={location.pathname === "/dashboard"}
            onClick={() => navigate("/dashboard")}
            icon={<DashboardIcon />}
          >
            Dashboard
          </MenuItem>

          {dashboardType === DASHBOARD_TYPE.SELLER && (
            <>
              <MenuItem
                active={isActive(["/dashboard/listing", "/dashboard/create"])}
                onClick={guardListing}
                icon={<ListingsIcon />}
              >
                Listings
              </MenuItem>
              <MenuItem
                active={isActive(["/dashboard/open-for-bids"])}
                onClick={() => navigate("/dashboard/open-for-bids")}
                icon={<OpenBidsIcon />}
              >
                Open bids
              </MenuItem>
              <MenuItem
                active={isActive(["/dashboard/disputes"])}
                onClick={() => navigate("/dashboard/disputes")}
                icon={<DisputesIcon />}
              >
                Disputes
              </MenuItem>
              <MenuItem
                active={isActive(["/dashboard/seller-payments"])}
                onClick={() => navigate("/dashboard/seller-payments")}
                icon={<PaymentsIcon />}
              >
                Payments & Tracking
              </MenuItem>
              <MenuItem
                active={isActive(["/dashboard/my-reviews"])}
                onClick={() => navigate("/dashboard/my-reviews")}
                icon={<ReviewsIcon />}
              >
                My Reviews
              </MenuItem>
              <MenuItem
                active={isActive(["/dashboard/followers"])}
                onClick={() => navigate("/dashboard/followers")}
                icon={<FollowersIcon />}
              >
                Followers
              </MenuItem>
            </>
          )}

          {dashboardType === DASHBOARD_TYPE.BUYER && (
            <>
              <MenuItem
                active={isActive(["/dashboard/watchlist"])}
                onClick={() => navigate("/dashboard/watchlist")}
                icon={<BookmarkIcon />}
              >
                Watchlist
              </MenuItem>
              <MenuItem
                active={isActive(["/dashboard/bids"])}
                onClick={() => navigate("/dashboard/bids")}
                icon={<BidsIcon />}
              >
                Bids
              </MenuItem>
              <MenuItem
                active={isActive(["/dashboard/buyer-payments"])}
                onClick={() => navigate("/dashboard/buyer-payments")}
                icon={<PaymentsIcon />}
              >
                Payments & Tracking
              </MenuItem>
              <MenuItem
                active={isActive([
                  "/dashboard/reviews",
                  "/dashboard/review",
                ])}
                onClick={() => navigate("/dashboard/reviews")}
                icon={<ReviewsIcon />}
              >
                Reviews
              </MenuItem>
              <MenuItem
                active={isActive(["/dashboard/following"])}
                onClick={() => navigate("/dashboard/following")}
                icon={<FollowingIcon />}
              >
                Following
              </MenuItem>
            </>
          )}

          <MenuItem
            active={isActive(["/dashboard/help-and-support"])}
            onClick={() => navigate("/dashboard/help-and-support")}
            icon={<HelpAndSupportIcon />}
          >
            Help & Support
          </MenuItem>
          <MenuItem
            active={isActive(["/dashboard/profile"])}
            onClick={() => navigate("/dashboard/profile")}
            icon={<ProfileIcon />}
          >
            Profile
          </MenuItem>

          {dashboardType === DASHBOARD_TYPE.SELLER && (
            <MenuItem
              active={isActive([
                "/dashboard/trustap-account",
                "/dashboard/trustap-account-callback",
              ])}
              onClick={() => navigate("/dashboard/trustap-account")}
              icon={<UserShield />}
            >
              Trustap account
            </MenuItem>
          )}
        </Menu>

        <div className="menuBtnHolder">
          <button
            type="button"
            className="sidebar__footerBtn sidebar__footerBtn--logout"
            onClick={() => setShowConfirm(true)}
          >
            <LogoutIcon />
            <span>Logout</span>
          </button>
          <button
            type="button"
            className="sidebar__footerBtn sidebar__footerBtn--danger"
            onClick={() => setShowDeleteAccountPopup(true)}
          >
            <DeleteAccountIcon />
            <span>Delete account</span>
          </button>
          <div className="sidebar__themeRow">
            <ThemeToggle />
          </div>
        </div>
      </SidebarPro>
      <DeleteAccountPopup
        show={showDeleteAccountPopup}
        onHide={() => setShowDeleteAccountPopup(false)}
      />
      <Confirm
        show={showConfirm}
        onNo={() => setShowConfirm(false)}
        onYes={handleLogout}
        title="Log out of Boll-e™?"
        body="You'll need to sign in again to bid, list, or message sellers."
        closeButtonText="Stay signed in"
        confirmButtonText="Log out"
        variant="primary"
        iconClass="bi-box-arrow-right"
      />
    </>
  );
};

export default Sidebar;
